/**
 * iter146 — Grouped, searchable crew member picker.
 *
 * A drop-in replacement for the plain `<select>` used in Shoot Crew
 * (Shoot Lead + per-role Member cells). Preserves the same value
 * contract (returns/receives `staff_id`) so assignment logic upstream
 * is untouched.
 *
 * Features:
 *   • Two groups: In-House Team ▸ Freelancers, split by
 *     `staff.employment_type`.
 *   • Alphabetical sort within each group (case-insensitive).
 *   • Live search across both groups (name / role).
 *   • Badge next to each name — 🏢 In-House or 👤 Freelancer.
 *   • Archived / inactive members hidden (`staff.active === false`).
 *   • Recently-selected staff IDs remembered per picker key via
 *     localStorage and surfaced at the top of the list.
 *   • Currently-selected staff always visible even if the search
 *     filter would exclude it.
 *
 * Props:
 *   - staff       {array}  Full staff list (all fields; component
 *                          filters out inactive itself).
 *   - value       {string} Selected staff_id ("" = None).
 *   - onChange    {fn}     `(newStaffId: string) => void`.
 *   - disabled    {bool}   Read-only.
 *   - placeholder {string} Text when value === "" (default "— Member —").
 *   - recentsKey  {string} localStorage key for the recents list.
 *                          Distinct pickers → distinct lists.
 *   - testid      {string} `data-testid` on the trigger button.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Building2, User, X as XIcon, ChevronDown, Check } from "lucide-react";

const MAX_RECENTS = 5;

function readRecents(key) {
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(`kbrj_crew_recents_${key}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeRecents(key, ids) {
  if (!key || typeof window === "undefined") return;
  try {
    localStorage.setItem(`kbrj_crew_recents_${key}`, JSON.stringify(ids.slice(0, MAX_RECENTS)));
  } catch { /* quota / disabled — ignore */ }
}

export default function CrewMemberPicker({
  staff = [],
  value = "",
  onChange,
  disabled = false,
  placeholder = "— Member —",
  recentsKey = "default",
  testid = "crew-picker",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState(() => readRecents(recentsKey));
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  // Refresh recents when the storage key changes (e.g., navigating between rows).
  useEffect(() => {
    setRecents(readRecents(recentsKey));
  }, [recentsKey]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Focus the search input the moment the popover opens.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);

  // Precompute active staff & split into groups, sorted alphabetically.
  const activeStaff = useMemo(
    () => (Array.isArray(staff) ? staff : []).filter((s) => s && s.active !== false),
    [staff]
  );
  const byId = useMemo(() => {
    const map = {};
    activeStaff.forEach((s) => { map[s.id] = s; });
    return map;
  }, [activeStaff]);

  const selectedStaff = value ? byId[value] : null;

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return activeStaff;
    return activeStaff.filter((s) => {
      const hay = `${s.name || ""} ${s.primary_skill || ""} ${s.employment_type || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [activeStaff, q]);

  // Always include the currently-selected staff even if the filter would hide it.
  const filteredWithSelected = useMemo(() => {
    if (!selectedStaff) return filtered;
    if (filtered.some((s) => s.id === selectedStaff.id)) return filtered;
    return [selectedStaff, ...filtered];
  }, [filtered, selectedStaff]);

  const inHouse = useMemo(
    () => filteredWithSelected
      .filter((s) => (s.employment_type || "in_house") === "in_house")
      .sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())),
    [filteredWithSelected]
  );
  const freelance = useMemo(
    () => filteredWithSelected
      .filter((s) => (s.employment_type || "in_house") === "freelance")
      .sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())),
    [filteredWithSelected]
  );

  const recentStaff = useMemo(() => {
    if (q) return []; // Hide recents while searching to keep the UI clean.
    return recents
      .map((id) => byId[id])
      .filter(Boolean);
  }, [recents, byId, q]);

  const commit = (newId) => {
    onChange?.(newId);
    setOpen(false);
    setQuery("");
    if (newId) {
      const nextRecents = [newId, ...recents.filter((x) => x !== newId)].slice(0, MAX_RECENTS);
      setRecents(nextRecents);
      writeRecents(recentsKey, nextRecents);
    }
  };

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`input py-1 text-xs w-full text-left flex items-center gap-1.5 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testid}
      >
        {selectedStaff ? (
          <>
            {(selectedStaff.employment_type || "in_house") === "freelance"
              ? <User className="w-3 h-3 text-amber-600" strokeWidth={2.2} />
              : <Building2 className="w-3 h-3 text-sky-600" strokeWidth={2.2} />}
            <span className="truncate">{selectedStaff.name}</span>
          </>
        ) : (
          <span className="text-stone-400">{placeholder}</span>
        )}
        <ChevronDown className="w-3 h-3 text-stone-400 ml-auto shrink-0" strokeWidth={2} />
      </button>

      {open && !disabled && (
        <div
          className="absolute left-0 z-40 mt-1 w-[280px] max-w-[92vw] bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden"
          data-testid={`${testid}-popover`}
        >
          <div className="p-2 border-b border-stone-100 relative">
            <Search className="w-3.5 h-3.5 text-stone-400 absolute left-4 top-1/2 -translate-y-1/2" strokeWidth={2} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team & freelancers…"
              className="input py-1 pl-7 pr-6 text-xs w-full"
              data-testid={`${testid}-search`}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-0.5 text-stone-400 hover:text-stone-700"
                title="Clear search"
              >
                <XIcon className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto py-1 text-xs">
            {value && (
              <button
                type="button"
                onClick={() => commit("")}
                className="w-full text-left px-3 py-1.5 text-stone-500 italic hover:bg-stone-50"
                data-testid={`${testid}-clear`}
              >
                — None —
              </button>
            )}
            {recentStaff.length > 0 && (
              <PickerGroup
                label="Recent"
                icon={null}
                testid={`${testid}-recent`}
              >
                {recentStaff.map((s) => (
                  <PickerRow key={`recent-${s.id}`} staff={s} selected={s.id === value} onSelect={commit} testid={`${testid}-recent-${s.id}`} />
                ))}
              </PickerGroup>
            )}
            {inHouse.length > 0 && (
              <PickerGroup
                label="In-House Team"
                icon={<Building2 className="w-3 h-3 text-sky-600" strokeWidth={2.2} />}
                testid={`${testid}-in-house`}
              >
                {inHouse.map((s) => (
                  <PickerRow key={`in-${s.id}`} staff={s} selected={s.id === value} onSelect={commit} testid={`${testid}-option-${s.id}`} />
                ))}
              </PickerGroup>
            )}
            {freelance.length > 0 && (
              <PickerGroup
                label="Freelancers"
                icon={<User className="w-3 h-3 text-amber-600" strokeWidth={2.2} />}
                testid={`${testid}-freelance`}
              >
                {freelance.map((s) => (
                  <PickerRow key={`fl-${s.id}`} staff={s} selected={s.id === value} onSelect={commit} testid={`${testid}-option-${s.id}`} />
                ))}
              </PickerGroup>
            )}
            {inHouse.length === 0 && freelance.length === 0 && (
              <div className="px-3 py-6 text-center text-stone-400 italic" data-testid={`${testid}-empty`}>
                No members match “{query}”.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PickerGroup({ label, icon, children, testid }) {
  return (
    <div className="mb-1" data-testid={testid}>
      <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1.5 bg-stone-50">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

function PickerRow({ staff, selected, onSelect, testid }) {
  const isFreelance = (staff.employment_type || "in_house") === "freelance";
  return (
    <button
      type="button"
      onClick={() => onSelect(staff.id)}
      className={`w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-stone-50 transition-colors ${selected ? "bg-stone-100 font-semibold" : ""}`}
      data-testid={testid}
    >
      {isFreelance
        ? <User className="w-3 h-3 text-amber-600 shrink-0" strokeWidth={2.2} />
        : <Building2 className="w-3 h-3 text-sky-600 shrink-0" strokeWidth={2.2} />}
      <span className="truncate flex-1">{staff.name || "Unnamed"}</span>
      {staff.primary_skill && (
        <span className="text-[9px] uppercase tracking-widest text-stone-400 truncate max-w-[90px]">
          {staff.primary_skill}
        </span>
      )}
      {selected && <Check className="w-3 h-3 text-emerald-600 shrink-0" strokeWidth={2.4} />}
    </button>
  );
}
